// @flow
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { type RouterHistory } from "react-router-dom";
import { type Dispatch } from "redux";
import { get } from "lodash";
import queryString from "query-string";
import ApolloContext from "@tvg/utils/apolloContext";
import ProgramPageWrapper, { onAccountBalanceClick } from "@fdr/program-page";
import URPProgramPage from "@tvg/pp";
import UrpPPToastManager from "@tvg/toast-manager";
import { type TalentPickData } from "@tvg/types/TalentPicks";
import { type User } from "@tvg/types/User";
import buildRaceUrl from "@tvg/formatter/url";
import mediator from "@tvg/mediator";
import { onVideoClick } from "@fdr/program-page-header";
import { FeedbackMessageContext } from "@fdr/utils/contextUtils";
import {
  setSelectionRequest,
  setBetAmount,
  setBetType,
  setStartBetting,
  setBetConfirmation,
  resetBetting,
  setPickAction,
  type Actions as SharedActions
} from "@tvg/shared-actions/BetActions";
import { Containers } from "@fdr/static-ui/BaseComponents";
import { RaceTrackDetails } from "@urp/racetracks";
import { isURP } from "@tvg/utils/generalUtils";
import { isXSell } from "@tvg/sh-utils/mobileUtils";
import RaceVideo from "@fdr/video";
import { FixedBanner } from "@fdr/atomic-ui/_molecules/MessageV2";
import withRouter from "@tvg/utils/withCustomRouter";
import { isFdr } from "@tvg/pp/src/utils/general";
import perf from "../utils/perf";
import type { TransitionState } from "../../types";
import { programPageNavigation, livePageNavigation } from "../utils/gtmUtils";
import { DesktopFdrVideoWrapper } from "../style/program-page-styled";

export type Props = {
  dispatch: Dispatch<SharedActions>,
  transitionState: TransitionState,
  wagerProfile: string,
  withRaces: boolean,
  withHandicapping: boolean,
  device: string,
  isFixed: boolean,
  talentPick: ?TalentPickData,
  showBetConfirmationModal: boolean,
  prevPath: string,
  router: {
    history: RouterHistory
  },
  nextRaces: {
    trackCode: string,
    trackName: string,
    number: string,
    status: {
      code: string
    }
  }[],
  hasWebSockets: boolean,
  isAccountCompliant?: boolean,
  userData: User,
  withVideo: boolean
};

type State = {
  hasEntered: boolean,
  trackAbbr: string,
  trackName: string,
  raceNumber: string,
  selectedRunner: string,
  channel: string,
  previousRaceNumber: string
};

const getRaceNumber = (race: string = ""): string => {
  if (race && race.length) {
    const raceNumber = race.split("-");
    return raceNumber.length > 0 ? raceNumber[1] : raceNumber[0];
  }
  return "1";
};

export class Program extends Component<Props, State> {
  static defaultProps = {
    wagerProfile: "FDR-Generic",
    prevPath: "",
    withRaces: false,
    withHandicapping: false,
    device: "mobile",
    talentPick: {},
    showBetConfirmationModal: false,
    nextRaces: [],
    isFixed: false,
    hasWebSockets: false
  };

  constructor(props: Props): void {
    super(props);
    this.state = {
      hasEntered: false,
      trackAbbr: "",
      trackName: "",
      raceNumber: "",
      selectedRunner: "",
      channel: "",
      previousRaceNumber: ""
    };
  }

  componentDidMount() {
    const urlParams = queryString.parse(get(this.props, "location.search"));
    const oldRaceNumber = get(this.props, "match.params.raceNumber");
    const raceNumber =
      (urlParams && urlParams.race) ||
      (oldRaceNumber && getRaceNumber(oldRaceNumber));

    if ((!raceNumber || !this.state.trackAbbr) && this.props.nextRaces.length) {
      this.redirectToNextRace(
        this.state.trackAbbr,
        this.state.trackName,
        raceNumber
      );
    }

    const wagerTypeId = get(this.props.talentPick, "wagerType.id", 0);
    const wagerTypeCode = get(
      this.props.talentPick,
      "wagerType.abbreviation",
      ""
    );
    const selections = get(this.props.talentPick, "selections", [[]]);
    const betAmount = get(this.props.talentPick, "amount", "");

    if (
      betAmount &&
      get(selections, "[0].length", 0) &&
      wagerTypeId &&
      wagerTypeCode
    ) {
      if (selections) {
        this.props.dispatch(setSelectionRequest(selections));
      }

      if (wagerTypeId && wagerTypeCode) {
        this.props.dispatch(
          // $FlowFixMe this needs to be fixed because the action requires a complete bet type with column count etc.etc. and not only the type {}
          setBetType({
            type: {
              id: +wagerTypeId,
              code: wagerTypeCode,
              name: ""
            }
          })
        );
      }

      if (betAmount) {
        this.props.dispatch(setBetAmount(+betAmount));
      }
      this.props.dispatch(setStartBetting(true));
      if (get(this.props, "match.params.showConfirmation", "")) {
        this.props.dispatch(setBetConfirmation(true));
      }
    } else {
      this.props.dispatch(setStartBetting(false));
    }
  }

  static getDerivedStateFromProps(nextProps: Props, state: State) {
    const trackAbbr = get(nextProps, "match.params.trackAbbr");
    const trackName = get(nextProps, "match.params.trackName");
    const channel = get(nextProps, "match.params.channel");
    const urlParams = queryString.parse(get(nextProps, "location.search"));
    const selectedRunner = urlParams && urlParams.selectedRunner;
    const raceNumber =
      urlParams && urlParams.race
        ? urlParams.race
        : getRaceNumber(get(nextProps, "match.params.raceNumber"));

    if (
      state.trackAbbr !== trackAbbr ||
      state.trackName !== trackName ||
      state.raceNumber !== raceNumber ||
      state.channel !== channel ||
      state.selectedRunner !== selectedRunner
    ) {
      return {
        trackAbbr,
        trackName,
        raceNumber,
        selectedRunner,
        channel,
        previousRaceNumber: state.raceNumber
      };
    }

    return null;
  }

  shouldComponentUpdate = (nextProps: Props) => {
    const urlParams = queryString.parse(get(this.props, "location.search"));
    const raceNumber =
      urlParams && urlParams.race
        ? urlParams.race
        : getRaceNumber(get(this.props, "match.params.raceNumber"));

    const selectedRunner = urlParams && urlParams.selectedRunner;

    const nextUrlPrams = queryString.parse(get(nextProps, "location.search"));
    const nextRace =
      nextUrlPrams && nextUrlPrams.race
        ? nextUrlPrams.race
        : getRaceNumber(get(nextProps, "match.params.raceNumber"));

    const nextSelectedRunner = nextUrlPrams && nextUrlPrams.selectedRunner;

    const trackAbbr = get(this.props, "match.params.trackAbbr");
    const nextTrack = get(nextProps, "match.params.trackAbbr");

    const nextProfile = get(nextProps, "userData.user.profile");
    const profile = get(this.props, "userData.user.profile");

    const nextRequestedSession = get(nextProps, "userData.hasRequested");
    const RequestedSession = get(this.props, "userData.hasRequested");

    const nextWithRaces = nextProps.withRaces;
    const { withRaces } = this.props;

    const nextWithHandicapping = nextProps.withHandicapping;
    const { withHandicapping } = this.props;

    const nextTransitionState = nextProps.transitionState;
    const oldTransitionState = this.props.transitionState;

    const nextNextRaces = nextProps.nextRaces;
    const oldNextRaces = this.props.nextRaces;

    return (
      nextTransitionState === "entered" &&
      (nextTrack !== trackAbbr ||
        nextRace !== raceNumber ||
        nextRequestedSession !== RequestedSession ||
        nextProfile !== profile ||
        selectedRunner !== nextSelectedRunner ||
        (nextWithRaces !== withRaces && nextWithRaces) ||
        nextWithHandicapping !== withHandicapping ||
        nextTransitionState !== oldTransitionState ||
        nextNextRaces !== oldNextRaces ||
        !this.state.hasEntered)
    );
  };

  componentDidUpdate = (prevProps: Props) => {
    if (this.props.transitionState === "entered") {
      // eslint-disable-next-line
      this.setState({
        hasEntered: true
      });

      perf.measure("page_render", { page: "program_page" });
    }

    // dispatch navigation gtm events
    const urlParams = queryString.parse(get(this.props, "location.search"));
    const isArrowNavigationChange = urlParams && !!urlParams.arrowNavigation;

    if (this.state.channel === "TVG" || this.state.channel === "TVG2") {
      livePageNavigation(prevProps, this.props);
    } else {
      programPageNavigation(prevProps, this.props, isArrowNavigationChange);
    }
  };

  componentWillUnmount() {
    this.props.dispatch(resetBetting());
    this.props.dispatch(setPickAction(null));
  }

  redirectToNextRace(trackAbbr: string, trackName: string, raceNumber: string) {
    const { nextRaces } = this.props;
    let nextRace = nextRaces.filter(
      (r) => r.status.code === "O" || r.status.code === "IC"
    )[0];

    if (trackAbbr && !raceNumber) {
      const nextRaceForTrack = nextRaces.find((r) => r.trackCode === trackAbbr);

      if (nextRaceForTrack) {
        nextRace = nextRaceForTrack;
      } else {
        nextRace = {
          trackCode: trackAbbr,
          trackName,
          number: 1
        };
      }
    }

    const nextRaceUrl =
      nextRace &&
      buildRaceUrl(nextRace.trackCode, nextRace.trackName, nextRace.number);

    if (nextRaceUrl) {
      // $FlowFixMe
      const { history } = this.props;
      // $FlowFixMe
      this.props.history.push(nextRaceUrl, history.location.state);
    }
  }

  render() {
    /*
     * On the server return null to avoid a memory leak
     * don't render the mask here because it will render the component inside the mask
     */
    if (!this.state.raceNumber || !this.state.trackAbbr) {
      return null;
    }

    // If there's no race number on the query parameters, e.g /racetracks/GP/gulfstream-park
    // should redirect to the Race Track Details page
    if (get(this.props, "location.search") === "") {
      return <RaceTrackDetails />;
    }
    return (
      <Containers.Page>
        <ApolloContext.Consumer>
          {(value) => (
            <React.Fragment>
              {
                // eslint-disable-next-line
                typeof window === "undefined" ? (
                  !isXSell()
                ) : isURP ? (
                  <FeedbackMessageContext.Consumer>
                    {({ bannerProps }) => {
                      return (
                        <React.Fragment>
                          <URPProgramPage
                            withFixedBanner={!!bannerProps && !isXSell()}
                            onAccountBalanceClick={() =>
                              onAccountBalanceClick(
                                !!this.props.isAccountCompliant
                              )
                            }
                            openLogin={() =>
                              mediator.base.dispatch({ type: "OPEN_LOGIN" })
                            }
                            openRegister={() =>
                              mediator.base.dispatch({ type: "OPEN_SIGNUP" })
                            }
                            onClickVideo={(videoFeedback: string, method) =>
                              onVideoClick({
                                dispatch: this.props.dispatch,
                                isUserLogged: this.props.userData?.logged,
                                isAccountCompliant:
                                  this.props.isAccountCompliant,
                                videoFeedback,
                                method
                              })
                            }
                            withVideo={this.props.withVideo}
                            fixedBanner={<FixedBanner {...bannerProps} />}
                            prevPath={this.props.prevPath}
                          >
                            {(props) => {
                              const FdrVideoWrapper =
                                isURP && isFdr()
                                  ? Fragment
                                  : DesktopFdrVideoWrapper;

                              return (
                                <FdrVideoWrapper>
                                  <RaceVideo {...props} />
                                </FdrVideoWrapper>
                              );
                            }}
                          </URPProgramPage>
                          <UrpPPToastManager />
                        </React.Fragment>
                      );
                    }}
                  </FeedbackMessageContext.Consumer>
                ) : (
                  <ProgramPageWrapper
                    trackAbbr={this.state.trackAbbr}
                    raceNumber={this.state.raceNumber}
                    trackName={this.state.trackName}
                    hasRequestedSession={get(
                      this.props,
                      "userData.hasRequested"
                    )}
                    wagerProfile={get(
                      this.props,
                      "userData.user.profile",
                      "FDR-Generic"
                    )}
                    prevPath={this.props.prevPath}
                    prevRaceNumber={this.state.previousRaceNumber}
                    selectedRunner={this.state.selectedRunner}
                    withRaces={this.props.withRaces}
                    withHandicapping={this.props.withHandicapping}
                    shouldUpdate={this.props.transitionState === "entered"}
                    liveTvg={this.state.channel === "TVG"}
                    liveTvg2={this.state.channel === "TVG2"}
                    device={this.props.device}
                    isFixed={this.props.isFixed}
                    hasWebSockets={this.props.hasWebSockets}
                    behgClient={value.behgClient}
                    gasClient={value.gasClient}
                  />
                )
              }
            </React.Fragment>
          )}
        </ApolloContext.Consumer>
      </Containers.Page>
    );
  }
}

export default withRouter(
  connect(
    (store) => ({
      currentRace: store.RaceProgram.currentRace,
      userData: store.userData,
      withRaces: store.RaceProgram.queryWithRaces,
      withHandicapping: store.RaceProgram.queryWithHandicapping,
      talentPick: store.RaceProgram.talentPick,
      showBetConfirmationModal: store.RaceProgram.showBetConfirmationModal,
      nextRaces: store.mtpStatus.mtpStatus,
      withVideo: get(store, "RaceProgramVideo.show", false),
      hasWebSockets: get(
        store,
        "capi.featureToggles.enableWebSocketsPP",
        false
      ),
      isAccountCompliant:
        get(store, "userData.isVerified") &&
        get(store, "userData.jurisdiction.accepted")
    }),
    (dispatch: Dispatch<SharedActions>) => ({ dispatch })
  )(Program)
);
